import React from 'react';
import { today, getLocalTimeZone } from '@internationalized/date';
import FormProvider from '../../primitives/form-provider';
import Input from '../../primitives/input';
import Select, { SelectOption } from '../../primitives/select';
import TextArea from '../../primitives/text-area';
import FormRegion from '../../primitives/form-region';
import FormFieldset from '../../primitives/form-fieldset';
import StyledButton from '../../primitives/styled-button';
import Typography from '../../primitives/typography';
import Divider from '../../primitives/divider';
import { useLocator } from '../../../contexts/franchise/locator-provider';
import { colors } from '../../primitives/colors';
import DatePicker from '../../primitives/date-picker';
import Spinner from '../../primitives/spinner';
import { formatPhoneNumber } from '../../utils/format-phone-link';
import { usStatesAndTerritories, canadianProvincesAndTerritories } from '../../../constants/states';
import Checkbox from '../../primitives/checkbox';
import RadioGroup, { RadioItem } from '../../primitives/radio-group';
import RowSet from '../../primitives/grid/row-set';
import { breakpoints } from '../../primitives/tokens';

const usAndCanStates = usStatesAndTerritories.concat(canadianProvincesAndTerritories);

const PREFIXES = {
    None: '-',
    'Mr.': 'Mr.',
    'Mrs.': 'Mrs.',
    'Ms.': 'Ms.',
};
const COMPLAINANT = {
    Customer: 'Customer',
    Franchise: 'Franchise',
    'Insurance Rep': 'Insurance Rep',
    Adjuster: 'Adjuster',
    Agent: 'Agent',
    Other: 'Other',
};

const RELATIONSHIP_TO_INSURED = {
    Adjuster: 'Adjuster',
    Agent: 'Agent',
    'Claims Rep': 'Claims Rep',
    Relative: 'Relative',
    Self: 'Self',
    'Vendor or Vendor Rep': 'Vendor or Vendor Rep',
};

const SERVICE_PERFORMED = {
    Biohazard: 'Biohazard',
    Contents: 'Contents',
    'Fire/Smoke': 'Fire/Smoke',
    Mold: 'Mold',
    'Water/Sewage': 'Water/Sewage',
    Wildfire: 'Wildfire',
};

// no 'deleted' loss types

type GeocodeRawHint = {
    text: string
    magicKey?: string
    place_id?: string
} & unknown;

type GeocodeEarthHint = {
    label: string
    data: GeocodeRawHint
};

interface FeedbackFormProps {
    submitButton?: string
    successMessage?: string
}

const placeholderStyleOverride = {
    '&:not(:placeholder-shown) ~ div label': {
        backgroundColor: colors.grey100,
    },
};

const hidePlaceholder = {
    '&:not(:placeholder-shown) ~ div label': {
        backgroundColor: 'transparent',
        color: 'transparent',
    },
};

const FeedbackForm = ({ submitButton, successMessage }: FeedbackFormProps): JSX.Element => {
    const [complainant, setComplainant] = React.useState<keyof typeof COMPLAINANT | null>(null);
    const [relationship, setRelationship] = React.useState<keyof typeof RELATIONSHIP_TO_INSURED | null>(null);

    const suggestionsRef = React.useRef<HTMLDivElement>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [suggestionQuery, setSuggestionQuery] = React.useState('');
    const [overrideFields, setOverrideFields] = React.useState<{
        address1: string
        address2: string,
        city: string
        state: string
        zip: string
    }>({
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
    });
    const [suggestions, setSuggestion] = React.useState<GeocodeEarthHint[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [formSubmitSuccess, setFormSubmitSuccess] = React.useState(false);
    const [formSubmitFail, setFormSubmitFail] = React.useState(false);
    const [loadingSuggestion, setLoadingSuggestion] = React.useState(false);

    const scrollToTop = (): void => {
        if (typeof window !== 'undefined') {
            window.scrollTo(0, 0);
        }
    };

    const { geo } = useLocator();

    const getSuggestions = React.useCallback(
        (value: string) => {
            const type = 'address';
            const url = `/api/arcgis-suggest?${new URLSearchParams({
                text: value,
                types: type,
                radius: '200000',
                latitude: `${geo?.latitude || ''}`,
                longitude: `${geo?.longitude || ''}`,
                category: 'Address',
            }).toString()}`;

            setLoadingSuggestion(true);

            fetch(url)
                .then(async (response) => {
                    if (!response.ok) {
                        throw new Error('Something went wrong with the call to geocode suggest');
                    }
                    const result: GeocodeRawHint[] = await response.json();
                    if (!Array.isArray(result)) {
                        return null;
                    }
                    setSuggestion(result.map(item => ({
                        data: item,
                        label: item.text,
                    })));
                    setLoadingSuggestion(false);
                    return null;
                })
                .catch((err) => {
                    // eslint-disable-next-line no-console
                    console.error(err);
                    setSuggestion([]);
                    setLoadingSuggestion(false);
                });
        },
        [geo?.latitude, geo?.longitude],
    );

    React.useEffect(() => {
        if (suggestionQuery) {
            // this length is arbitrary.open to suggestions
            if (suggestionQuery.length > 3) {
                getSuggestions(suggestionQuery);
            } else {
                setSuggestion([]);
                setLoadingSuggestion(false);
            }
        } else {
            // this is for when there's an empty string as a value
            setSuggestion([]);
        }
    }, [getSuggestions, suggestionQuery]);

    const selectSuggestion = (hint: GeocodeRawHint): void => {
        setLoadingSuggestion(true);
        const url = `/api/geocode?${(new URLSearchParams({
            placeId: hint.place_id || '',
            magicKey: hint.magicKey || '',
            address: hint.text || '',
        })).toString()}`;
        fetch(url)
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error('Something went wrong with the call to geocode');
                }
                const data = await response.json();
                if (data) {
                    setSuggestion([]);
                    setOverrideFields({
                        address1: data.address1,
                        address2: data.address2,
                        city: data.city,
                        state: data.stateShort,
                        zip: data.zip,
                    });
                }
                setLoadingSuggestion(false);
                return null;
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.error(err);
                setSuggestion([]);
                setLoadingSuggestion(false);
            });
    };

    const onSubmit = (e: Record<string, string>): void => {
        setLoading(true);

        // submit

        try {
            e['contact-phone'] = formatPhoneNumber(e['contact-phone']);

            if (relationship === 'Self') {
                e['insured-given-name'] = e['given-name'];
                e['insured-family-name'] = e['family-name'];
                e['insured-phone'] = e['home-phone'] || e['cell-phone'] || e['work-phone'];
            }

            if (e.title === '-') {
                e.title = '';
            }

            fetch('/api/forms/feedback', {
                method: 'POST',
                body: JSON.stringify(e),
                headers: {
                    'Content-type': 'application/json; charset=UTF-8',
                },
            })
                .then((res) => {
                    if (!res.ok) {
                        throw new Error('Something went wrong with the feedback-form api');
                    }
                    return res.json();
                }).then((data) => {
                    setLoading(false);
                    if ('success' in data) {
                        scrollToTop();
                        if (data.success) {
                            setFormSubmitSuccess(true);
                        } else {
                            setFormSubmitFail(true);
                        }
                    }
                    return null;
                })
                .catch((err) => {
                    scrollToTop();
                    // eslint-disable-next-line no-console
                    console.log('err', err);
                    setLoading(false);
                    setFormSubmitFail(true);
                });
        } catch (err) {
            scrollToTop();
            // eslint-disable-next-line no-console
            console.log('err', err);
            setLoading(false);
            setFormSubmitFail(true);
        }
    };

    return (!formSubmitSuccess && !formSubmitFail) ? (
        <FormProvider disabled={loading} onSubmit={onSubmit} values={overrideFields}>
            <RowSet breakpoints={{
                dt: { between: 32 },
                mb: { between: 16 },
            }}
            >
                <FormRegion description="Contact Information">
                    <FormFieldset alignLeft name="">
                        <div css={{
                            display: 'inline-flex',
                            gap: 'inherit',
                            width: '100%',
                            flexWrap: 'wrap',
                            // border: "3px dashed green",
                            '@media (min-width: 1024px)': {
                                // border: "3px dashed red",
                                flexWrap: 'nowrap',
                            },

                        }}
                        >
                            <div css={{
                                maxWidth: '96px',
                                [breakpoints.mb]: {
                                    maxWidth: 'unset',
                                    width: '100%',
                                },
                            }}
                            >
                                <Select name="title" placeholder='Prefix' css={hidePlaceholder}>
                                    {Object.entries(PREFIXES).map(([key, value]) => (
                                        <SelectOption key={key} value={value}>
                                            {key}
                                        </SelectOption>
                                    ))}
                                </Select>
                            </div>

                            <div css={{
                                width: '50%',
                                [breakpoints.tb]: {
                                    width: '100%',
                                },
                            }}
                            >
                                <Input autoFocus name="given-name" placeholder="Contact First Name" autoComplete="given-name" required css={placeholderStyleOverride} />
                            </div>
                            <div css={{
                                width: '50%',
                                [breakpoints.tb]: {
                                    width: '100%',
                                },
                            }}
                            >
                                <Input name="family-name" placeholder="Contact Last Name" autoComplete="family-name" required css={placeholderStyleOverride} />
                            </div>
                        </div>

                    </FormFieldset>
                    <FormFieldset alignLeft name="">
                        <div css={{
                            display: 'inline-flex',
                            gap: 'inherit',
                            width: '100%',
                            flexWrap: 'nowrap',
                            [breakpoints.tb]: {
                                flexWrap: 'wrap',
                            },

                        }}
                        >
                            <div css={{
                                width: '50%',
                                [breakpoints.tb]: {
                                    width: '100%',
                                },
                            }}
                            >
                                <Input
                                    name="home-phone"
                                    type="tel"
                                    validationRegex={/^\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/g}
                                    placeholder="Home Phone Number"
                                    autoComplete="tel"
                                    css={hidePlaceholder}
                                />
                            </div>
                            <div css={{
                                width: '50%',
                                [breakpoints.tb]: {
                                    width: '100%',
                                },
                            }}
                            >
                                <Input
                                    name="cell-phone"
                                    type="tel"
                                    validationRegex={/^\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/g}
                                    placeholder="Cell Phone Number"
                                    autoComplete="tel"
                                    css={hidePlaceholder}
                                />
                            </div>
                        </div>
                        <div css={{
                            display: 'inline-flex',
                            gap: 'inherit',
                            width: '100%',
                            flexWrap: 'nowrap',
                            [breakpoints.tb]: {
                                flexWrap: 'wrap',
                            },
                        }}
                        >
                            <div css={{
                                width: '50%',
                                [breakpoints.tb]: {
                                    width: '100%',
                                },
                            }}
                            >
                                <Input
                                    name="work-phone"
                                    type="tel"
                                    validationRegex={/^\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/g}
                                    placeholder="Work Phone Number"
                                    autoComplete="tel"
                                    css={hidePlaceholder}
                                />
                            </div>
                            <div css={{
                                width: '50%',
                                [breakpoints.tb]: {
                                    width: '100%',
                                },
                            }}
                            >
                                <Input required name="email" placeholder="Email" autoComplete="email" css={hidePlaceholder} />
                            </div>
                        </div>
                    </FormFieldset>
                    <FormFieldset alignLeft name="">
                        <Select
                            required
                            name="relationship-to-insured"
                            placeholder='Relationship to Insured'
                            css={hidePlaceholder}
                            onChange={(e) => {
                                setRelationship(e as keyof typeof RELATIONSHIP_TO_INSURED);
                            }}
                        >
                            {Object.entries(RELATIONSHIP_TO_INSURED).map(([key, value]) => (
                                <SelectOption key={key} value={value}>
                                    {key}
                                </SelectOption>
                            ))}
                        </Select>
                    </FormFieldset>
                    {relationship?.toString() === 'Relative'
                        && (
                            <FormFieldset alignLeft name="">
                                <div
                                    css={{ width: '100%' }}
                                >
                                    <Checkbox
                                        LabelFontSize='sm'
                                        size="sm"
                                        name='opt-in'
                                        required
                                    >
                                        If relative is selected, individual must be Power of Attorney and present documentation
                                    </Checkbox>
                                </div>
                            </FormFieldset>
                        )}

                    <FormRegion description="Insured Information">
                        {relationship && relationship !== 'Self' && (
                            <>
                                <FormFieldset alignLeft name="">
                                    <div css={{
                                        display: 'inline-flex',
                                        gap: 'inherit',
                                        width: '100%',
                                        flexWrap: 'nowrap',
                                        [breakpoints.tb]: {
                                            flexWrap: 'wrap',
                                        },
                                    }}
                                    >
                                        <div css={{
                                            width: '50%',
                                            [breakpoints.tb]: {
                                                width: '100%',
                                            },
                                        }}
                                        >
                                            <Input name="insured-given-name" placeholder="Insured's First Name" required css={placeholderStyleOverride} />
                                        </div>
                                        <div css={{
                                            width: '50%',
                                            [breakpoints.tb]: {
                                                width: '100%',
                                            },
                                        }}
                                        >
                                            <Input name="insured-family-name" placeholder="Insured's Last Name" required css={placeholderStyleOverride} />
                                        </div>
                                    </div>
                                </FormFieldset>
                                <FormFieldset alignLeft name="">
                                    <Input name="insured-phone" type="tel" validationRegex={/^\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/g} placeholder="Insured's Phone Number" css={hidePlaceholder} />
                                </FormFieldset>
                            </>
                        )}

                        <FormFieldset alignLeft name="">
                            <Input name="insurance-claim" placeholder="Insurance Claim #" css={placeholderStyleOverride} />
                        </FormFieldset>
                        <FormFieldset alignLeft name="">
                            <div css={{
                                display: 'inline-flex',
                                gap: 'inherit',
                                width: '100%',
                                flexWrap: 'nowrap',
                                [breakpoints.tb]: {
                                    flexWrap: 'wrap',
                                },

                            }}
                            >
                                <div css={{
                                    width: '50%',
                                    [breakpoints.tb]: {
                                        width: '100%',
                                    },
                                }}
                                >
                                    <Input name="insurance-company" placeholder="Insurance Company" css={placeholderStyleOverride} />
                                </div>
                                <div css={{
                                    width: '50%',
                                    [breakpoints.tb]: {
                                        width: '100%',
                                    },
                                }}
                                >

                                    <Input name="insurance-rep" placeholder="Insurance Rep" css={placeholderStyleOverride} />
                                </div>
                            </div>
                        </FormFieldset>
                    </FormRegion>
                </FormRegion>
                <FormRegion description="Service Information">
                    <FormFieldset alignLeft name="">
                        <div css={{

                            width: 'calc(100% - 112px - 8px)',
                            position: 'relative',
                            [breakpoints.mb]: {
                                width: '100%',
                            },
                        }}
                        >
                            <div css={{
                                position: 'relative',
                                zIndex: 10,
                                borderRadius: '4px',
                                width: '100%',
                                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0), 0 4px 6px -2px rgba(0, 0, 0, 0)',
                                transition: '0.3s box-shadow',
                                input: {
                                    backgroundColor: suggestionQuery ? colors.grey100 : colors.grey200,
                                },
                                '&:focus-within': {
                                    '.suggestions': {
                                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                                        display: (suggestions && suggestions.length > 0) ? 'block' : 'none',
                                        borderBottomLeftRadius: '4px',
                                        borderBottomRightRadius: '4px',
                                    },
                                    input: {
                                        borderBottomLeftRadius: (suggestions && suggestions.length > 0) ? '0px' : '4px',
                                        borderBottomRightRadius: (suggestions && suggestions.length > 0) ? '0px' : '4px',
                                    },
                                },
                            }}
                            >
                                <div
                                    css={{
                                        position: 'absolute',
                                        right: '30px',
                                        zIndex: 1,
                                        top: '15px',
                                    }}
                                    style={{
                                        opacity: loadingSuggestion ? 1 : 0,
                                    }}
                                >
                                    <Spinner
                                        size="sm"
                                    />
                                </div>
                                <Input
                                    ref={inputRef}
                                    name="address1"
                                    type="text"
                                    css={placeholderStyleOverride}
                                    placeholder="Property Address"
                                    onChange={(e) => {
                                        if (e?.currentTarget?.value) {
                                            setSuggestionQuery(e.currentTarget.value);
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'ArrowDown' && suggestions && suggestions.length > 0) {
                                            e.preventDefault();
                                            suggestionsRef.current?.querySelector('button')?.focus();
                                        } else if (e.key === 'ArrowUp' && suggestions && suggestions.length > 0) {
                                            e.preventDefault();
                                            if (suggestionsRef.current) {
                                                const selector = suggestionsRef.current?.querySelector('button:last-child');
                                                if (selector && 'focus' in selector && typeof selector.focus === 'function') {
                                                    selector.focus();
                                                }
                                            }
                                        }
                                    }}
                                />
                                {suggestions && suggestions.length > 0 && (
                                    <div
                                        className="suggestions"
                                        css={{
                                            display: 'none',
                                            maxWidth: '100%',
                                            marginTop: '2px',
                                            position: 'absolute',
                                            backgroundColor: colors.shadesWhite,
                                            [breakpoints.mb]: {
                                                width: 'calc(100vw - 65px)',
                                                maxWidth: 'calc(100vw - 65px)',
                                                left: 0,
                                            },
                                        }}
                                        ref={suggestionsRef}
                                        tabIndex={-1}
                                        role="listbox"
                                    >
                                        {suggestions.map(item => (
                                            <button
                                                key={item.label}
                                                type="button"
                                                css={{
                                                    display: 'flex',
                                                    paddingLeft: '16px',
                                                    paddingRight: '16px',
                                                    alignItems: 'center',
                                                    width: '100%',
                                                    height: '40px',
                                                    borderStyle: 'none',
                                                    cursor: 'pointer',
                                                    userSelect: 'none',
                                                    transition: '0.2s',
                                                    '&:hover, &:focus': {
                                                        backgroundColor: colors.grey200,
                                                        paddingLeft: '20px',
                                                    },
                                                    '&:last-of-type': {
                                                        borderBottomLeftRadius: '4px',
                                                        borderBottomRightRadius: '4px',
                                                    },
                                                }}
                                                onClick={() => {
                                                    selectSuggestion(item.data);
                                                }}
                                                onKeyDown={(e) => {
                                                    // should also handle the escape kye
                                                    if (e.key === 'ArrowDown') {
                                                        e.preventDefault();
                                                        const currentSuggestionElement = e.currentTarget;
                                                        const nextSuggestionElement = currentSuggestionElement
                                                            .nextElementSibling as HTMLButtonElement | undefined;
                                                        if (nextSuggestionElement) {
                                                            nextSuggestionElement.focus();
                                                        } else if (inputRef.current) {
                                                            inputRef.current.focus();
                                                        }
                                                    }

                                                    if (e.key === 'ArrowUp') {
                                                        e.preventDefault();
                                                        const currentSuggestionElement = e.currentTarget;
                                                        const prevSuggestionElement = currentSuggestionElement
                                                            .previousElementSibling as HTMLButtonElement | undefined;

                                                        if (prevSuggestionElement) {
                                                            prevSuggestionElement.focus();
                                                        } else if (inputRef.current) {
                                                            inputRef.current.focus();
                                                        }
                                                    }
                                                }}
                                            >
                                                <span css={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                                >
                                                    {item.label}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div css={{

                            '@media (min-width: 375px)': {
                                width: '112px',
                            },
                        }}
                        >
                            <Input
                                name="address2"
                                placeholder="Apt./Suite"
                                autoComplete="address-line2"
                                css={placeholderStyleOverride}
                            />
                        </div>
                        <div css={{
                            '@media (min-width: 375px)': {
                                width: 'calc(100% - 112px - 8px)',
                            },
                            '@media (min-width: 1024px)': {
                                width: 'calc(50% - 6px)',
                            },
                        }}
                        >

                            <Input
                                name="city"
                                placeholder="City"
                                autoComplete="address-level2"
                                css={placeholderStyleOverride}
                            />
                        </div>
                        <div css={{
                            '@media (min-width: 375px)': {

                                width: 'calc(50% - 4px)',
                            },
                            '@media (min-width: 1024px)': {
                                width: 'calc(25% - 6px)',
                            },
                        }}
                        >

                            <Select name="state" placeholder='State' css={hidePlaceholder}>
                                {usAndCanStates.map(state => (
                                    <SelectOption key={state.abbreviation} value={state.abbreviation}>
                                        {state.abbreviation}
                                    </SelectOption>
                                ))}
                            </Select>
                        </div>
                        <div css={{
                            '@media (min-width: 375px)': {

                                width: 'calc(50% - 4px)',
                            },

                            '@media (min-width: 1024px)': {
                                width: 'calc(25% - 6px)',
                            },
                        }}
                        >

                            <Input
                                name="zip"
                                placeholder="Zip"
                                autoComplete="postal-code"
                                css={placeholderStyleOverride}
                            />
                        </div>
                    </FormFieldset>

                    <FormFieldset alignLeft name="">
                        <div css={{
                            display: 'inline-flex',
                            gap: 'inherit',
                            width: '100%',
                            flexWrap: 'wrap',
                            // border: "3px dashed green",
                            '@media (min-width: 1024px)': {
                                // border: "3px dashed red",
                                flexWrap: 'nowrap',
                            },

                        }}
                        >
                            <div css={{
                                width: '50%',
                                [breakpoints.tb]: {
                                    width: '100%',
                                },
                            }}
                            >

                                <Input name="franchise" placeholder="SERVPRO Franchise Name" required css={placeholderStyleOverride} />
                            </div>
                        </div>
                        <div css={{
                            width: '50%',
                            [breakpoints.tb]: {
                                width: '100%',
                            },
                        }}
                        >

                            <Input name="servpro-reference-number" placeholder="SERVPRO Reference Number" css={placeholderStyleOverride} />
                        </div>

                    </FormFieldset>
                    <FormFieldset alignLeft name="">
                        <Typography css={{ width: '100%' }} fontSize={{ dt: 'sm' }}>
                            To identify the franchise name, please refer to the paperwork or business card left by the franchise.
                            <Divider css={{
                                marginTop: '16px',
                            }}
                            />
                        </Typography>
                    </FormFieldset>
                    <FormFieldset alignLeft name="Date and Type of Service Performed">
                        {/* Loss Date / Time here */}
                        <div css={{
                            display: 'inline-flex',
                            gap: 'inherit',
                            width: '100%',
                            flexWrap: 'wrap',
                            // border: "3px dashed green",
                            '@media (min-width: 1024px)': {
                                // border: "3px dashed red",
                                flexWrap: 'nowrap',
                            },

                        }}
                        >
                            <div css={{
                                width: '50%',
                                [breakpoints.tb]: {
                                    width: '100%',
                                },
                            }}
                            >
                                <DatePicker name="service-date" maxValue={today(getLocalTimeZone())} />
                            </div>
                            <div css={{
                                width: '50%',
                                [breakpoints.tb]: {
                                    width: '100%',
                                },
                            }}
                            >

                                <Select name="service-performed" placeholder='Service Performed' css={hidePlaceholder}>
                                    {Object.entries(SERVICE_PERFORMED).map(([key, value]) => (
                                        <SelectOption key={key} value={value}>
                                            {key}
                                        </SelectOption>
                                    ))}
                                </Select>
                            </div>
                        </div>

                    </FormFieldset>
                    <FormFieldset alignLeft name="">
                        <Typography css={{ width: '100%' }} fontSize={{ dt: 'sm' }}>
                            If your loss was related to a death or bloodborne pathogens, please select ‘Biohazard’ in the Service Performed dropdown
                            <Divider css={{
                                marginTop: '16px',
                            }}
                            />
                        </Typography>
                    </FormFieldset>
                    <FormFieldset alignLeft name="Structure Type">
                        <Select name="structure-type" placeholder='Residential or Commercial?' required css={hidePlaceholder}>
                            <SelectOption value="Residential">
                                Residential
                            </SelectOption>
                            <SelectOption value="Commercial">
                                Commercial
                            </SelectOption>
                        </Select>
                    </FormFieldset>
                    <FormFieldset alignLeft name="Was reconstruction performed by the franchise?">
                        <RadioGroup name="reconstruction-performed-by-franchise" required>
                            <RadioItem value="Yes">
                                Yes
                            </RadioItem>
                            <RadioItem value="No">
                                No
                            </RadioItem>
                        </RadioGroup>
                    </FormFieldset>
                </FormRegion>

                <FormRegion description="Feedback">
                    <FormFieldset alignLeft name="">
                        <div css={{
                            display: 'inline-flex',
                            gap: 'inherit',
                            width: '100%',
                            flexWrap: 'wrap',
                            // border: "3px dashed green",
                            '@media (min-width: 1024px)': {
                                // border: "3px dashed red",
                                flexWrap: 'nowrap',
                            },

                        }}
                        >
                            <div css={{
                                width: '100%',
                                '@media (min-width: 1024px)': {
                                    width: '50%',
                                },
                            }}
                            >

                                <Select
                                    name="complainant"
                                    placeholder='Complainant is:'
                                    css={hidePlaceholder}
                                    onChange={(e) => {
                                        setComplainant(e as keyof typeof COMPLAINANT);
                                    }}
                                >
                                    {Object.entries(COMPLAINANT).map(([key, value]) => (
                                        <SelectOption key={key} value={value}>
                                            {key}
                                        </SelectOption>
                                    ))}
                                </Select>
                            </div>
                            {complainant?.toString() === 'Other'
                                && (
                                    <div css={{
                                        width: '100%',
                                        '@media (min-width: 1024px)': {
                                            width: '50%',
                                        },
                                    }}
                                    >

                                        <Input name="complainant-other" placeholder="Other" required css={placeholderStyleOverride} />
                                    </div>
                                )}
                        </div>
                    </FormFieldset>

                    <FormFieldset alignLeft name="Description">
                        <TextArea name="description" placeholder="" css={placeholderStyleOverride} />
                    </FormFieldset>
                </FormRegion>
                <FormRegion description="">
                    <FormFieldset alignLeft name="">
                        <StyledButton type="submit" loading={loading}>
                            {submitButton || 'Submit'}
                        </StyledButton>
                    </FormFieldset>
                </FormRegion>
            </RowSet>
        </FormProvider>
    ) : (
        <>
            {formSubmitSuccess && (
                <div
                    style={{ paddingTop: '16px' }}
                    /* eslint-disable-next-line react/no-danger */
                    dangerouslySetInnerHTML={{ __html: successMessage || '' }}
                />
            )}
            {formSubmitFail && <div css={{ color: colors.statesError, paddingTop: '16px' }}>Something went wrong with your submission.</div>}
        </>
    );
};

export default FeedbackForm;
